// 9-stage horizontal award lifecycle arc — RFI → SOURCES SOUGHT → DRAFT RFP →
// RFP → Q&A → PROPOSAL → EVAL → AWARD → PERFORMANCE.
// Past stages: filled grey dot. Current: filled blue square + ring.
// Future: hollow grey square.
//
// UNKNOWN state (stage < 0): the inputs didn't justify claiming a lifecycle
// position. We render the full 9-stage track faded, no current-stage marker,
// and replace the labels with "—" indicators. The label row gains a "STAGE
// UNKNOWN — insufficient signal" caption so the operator knows the absence
// of a highlighted stage is a real claim, not a render bug.
//
// Why faded-9 instead of a 10th "UNKNOWN" band: the lifecycle is an ordering
// claim. UNKNOWN is the *absence* of an ordering claim, not a new position
// after PERFORMANCE. A 10th dot would imply UNKNOWN comes after performance,
// which is the opposite of what we mean.

const STAGES = [
  'RFI',
  'SOURCES SOUGHT',
  'DRAFT RFP',
  'RFP',
  'Q&A',
  'PROPOSAL',
  'EVAL',
  'AWARD',
  'PERFORMANCE',
];

export function AwardArc({ stage }: { stage: number }) {
  const unknown = stage < 0;
  const clamped = unknown ? -1 : Math.max(0, Math.min(STAGES.length - 1, stage));
  // Faded track + faded labels when we don't know the stage.
  const trackColor = unknown ? 'var(--fg-5, var(--fg-4))' : 'var(--fg-4)';
  return (
    <div
      style={{
        height: 56,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        padding: '0 16px',
        opacity: unknown ? 0.45 : 1,
      }}
      title={unknown ? 'Stage UNKNOWN — insufficient signal to classify' : undefined}
      data-stage={unknown ? 'unknown' : String(clamped)}
    >
      <div style={{ position: 'relative', height: 24 }}>
        {/* full track */}
        <div
          style={{
            position: 'absolute',
            left: 6,
            right: 6,
            top: 11,
            height: 1,
            background: trackColor,
          }}
        />
        {/* completed track — suppressed when unknown */}
        {!unknown && (
          <div
            style={{
              position: 'absolute',
              left: 6,
              top: 11,
              height: 1,
              background: 'var(--primary)',
              width: `calc(${(clamped / (STAGES.length - 1)) * 100}% - ${12 * (clamped / (STAGES.length - 1))}px)`,
            }}
          />
        )}
        {/* dots */}
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          {STAGES.map((s, i) => {
            const past = !unknown && i < clamped;
            const cur = !unknown && i === clamped;
            return (
              <div
                key={s}
                title={unknown ? 'stage unknown' : s}
                style={{
                  position: 'relative',
                  width: 12,
                  height: 12,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {cur ? (
                  <>
                    <div style={{ width: 8, height: 8, background: 'var(--primary)' }} />
                    <div
                      style={{
                        position: 'absolute',
                        inset: -3,
                        border: '1px solid var(--primary-line)',
                      }}
                    />
                  </>
                ) : past ? (
                  <div style={{ width: 6, height: 6, background: 'var(--fg-3)' }} />
                ) : (
                  <div style={{ width: 6, height: 6, border: '1px solid var(--fg-4)' }} />
                )}
              </div>
            );
          })}
        </div>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: 4 }}>
        {STAGES.map((s, i) => (
          <div
            key={s}
            className="meta mono"
            style={{
              width: 12,
              textAlign: 'center',
              color: unknown
                ? 'var(--fg-4)'
                : i === clamped
                  ? 'var(--primary)'
                  : i < clamped
                    ? 'var(--fg-2)'
                    : 'var(--fg-4)',
              fontSize: 9,
              letterSpacing: 0.4,
              whiteSpace: 'nowrap',
              transform: 'translateX(-44%)',
            }}
          >
            {s}
          </div>
        ))}
      </div>
      {unknown && (
        <div
          className="meta mono"
          style={{
            position: 'absolute',
            fontSize: 9,
            letterSpacing: 0.6,
            color: 'var(--fg-3)',
            transform: 'translateY(2px)',
            pointerEvents: 'none',
          }}
        >
          STAGE UNKNOWN
        </div>
      )}
    </div>
  );
}
